import { useEffect, useRef, useState } from 'react';

import { useRefState } from '@/hooks/use-ref-state';

import type { IResponse } from '@/utils/fetch';

export interface IFetchResult<TItem> {
    cursor?: string;
    list?: TItem[];
    hasMore?: boolean;
}

interface IUseInfiniteListStatusOptions<TResponseData, IRequestQuery, TItem> {
    fetcher: (params: IRequestQuery) => Promise<IResponse<TResponseData>>;
    initialCursor?: string;
    initialHasMore?: boolean;
    queryParams?: Record<string, unknown>;
    transformResponseData?: (data: TResponseData) => IFetchResult<TItem>;
}

export interface IUseInfiniteListStatusResult<TItem> {
    isError: boolean;
    isLoading: boolean;
    isEmpty: boolean;
    hasMore: boolean;
    list: TItem[];
    loadMore: () => Promise<void>;
}

export function useInfiniteListStatus<TItem, TResponseData, IRequestQuery>({
    fetcher,
    initialCursor = '',
    initialHasMore = true,
    queryParams = {},
    transformResponseData,
}: IUseInfiniteListStatusOptions<TResponseData, IRequestQuery, TItem>): IUseInfiniteListStatusResult<TItem> {
    const [isError, setIsError] = useState(false);
    const [isLoading, isLoadingRef, setIsLoading] = useRefState(true);
    const [isEmpty, setIsEmpty] = useState(false);
    const [hasMore, setHasMore] = useState(initialHasMore);
    const cursorRef = useRef(initialCursor);
    const [list, setList] = useState<TItem[]>([]);

    const loadMore = async () => {
        try {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const { data } = await fetcher({
                cursor: cursorRef.current,
                ...queryParams,
            } as IRequestQuery);
            // 解构错误， 接口返回数据结构不正确
            if (!data) {
                throw new Error('No data returned');
            }
            // const { cursor: newCursor, list, has_next } = data;
            // const parsedData = data as unknown as IFetchResult<TItem>;
            // 转换数据，兼容接口不同字段， 如 list 、 items 、 record_list 等
            const transformResult = transformResponseData ? transformResponseData(data) : data;
            const {
                cursor: newCursor = '',
                list: newList = [],
                hasMore: nextHasMore = false,
            } = transformResult as IFetchResult<TItem>;

            // 重置错误状态
            setIsError(false);

            // 加载时，数据为空，显示空页
            if (newList?.length === 0 && isLoadingRef.current) {
                setIsEmpty(true);
                return;
            }

            // 重置数据
            setIsEmpty(false);
            // 更新列表
            setList(prev => [...prev, ...((newList ?? []) as TItem[])]);
            // 更新游标
            cursorRef.current = newCursor;
            // 更新是否有更多数据
            setHasMore(nextHasMore);
        } catch {
            // 仅加载时错误显示兜底页
            if (isLoadingRef.current) {
                setIsError(true);
            }
            // 抛出错误，供 antd retry 功能使用
            throw new Error('No data returned');
        } finally {
            // 只要请求结束，就取消loading态
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadMore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        isError,
        isLoading,
        isEmpty,
        hasMore,
        list,
        loadMore,
    };
}
